import { Link } from "react-router-dom";
import {
  resolveOrderLoadStatus,
  type HistoricalLoadStatus,
} from "@/lib/order-load-status";
import { Truck, Package, Users, CheckCircle, ExternalLink, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getTrailerTypeLabel, type TrailerTypeRecord } from "@/hooks/useTrailerTypes";
import { useTenant } from "@/hooks/useTenant";
import { getAuroraTripUrl } from "@/lib/aurora-trip-url";
import type { Order, Customer, Tractor, Driver } from "@/types/tms";

type OrderWithRelations = Order & {
  billing_party?: Customer | null;
  assigned_tractor?: Tractor | null;
  assigned_operator?: Driver | null;
  assigned_operator_2?: Driver | null;
};

interface Props {
  order: OrderWithRelations;
  trailerTypes: TrailerTypeRecord[] | undefined;
  trailerLoadStatus: string | null | undefined;
  /** At-delivery load-status snapshot; delivered orders prefer it over the
   * trailer's live state (see src/lib/order-load-status.ts). */
  loadStatusHistorical?: HistoricalLoadStatus | null;
  trailerTypeIdFallback?: string | null;
  /** Hide per-leg fields (Tractor, Operators, External Trip ID) when showing
   * a parent order overview that has child legs. */
  hideLegSpecific?: boolean;
  eapiTripVerified?: boolean | null;
  recurringTemplate?: { unique_id: string } | null;
  /** Smaller text & padding for the side sheet variant. */
  compact?: boolean;
  /** When rendering a multi-leg parent Overview, pass the per-leg trailer
   * assignments so the Equipment card can reflect leg trailers rather than
   * the (legacy) parent-order-level trailer_id mirror. */
  legTrailers?: Array<{
    leg_sequence: number;
    trailer_id: string | null;
    trailer_type_id?: string | null;
  }>;
  /** Callback to regenerate (abandon + recreate) the external trip. When
   * omitted the regenerate button is hidden. */
  onRegenerateExternalTrip?: () => void;
  /** When true, shows the regenerate button in a spinning state and disables it. */
  regeneratingExternalTrip?: boolean;
}

function Row({ label, value, compact }: { label: string; value: React.ReactNode; compact?: boolean }) {
  return (
    <div className={`flex justify-between items-start ${compact ? "py-1.5" : "py-2"}`}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] break-words">{value || "—"}</span>
    </div>
  );
}

function SectionCard({
  title,
  icon,
  compact,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className={compact ? "pb-2" : "pb-3"}>
        <CardTitle className={`${compact ? "text-sm" : "text-base"} flex items-center gap-2`}>
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">{children}</CardContent>
    </Card>
  );
}

function TrailerSubCard({
  compact,
  heading,
  trailerId,
  trailerTypeLabel,
  loadLabel,
  temperature,
  seal,
}: {
  compact?: boolean;
  heading?: string;
  trailerId: string | null;
  trailerTypeLabel?: string | null;
  loadLabel?: string | null;
  temperature?: string | null;
  seal?: string | null;
}) {
  return (
    <div className={`border rounded-lg ${compact ? "p-2.5" : "p-3"} space-y-1`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          {heading && (
            <span className="text-xs text-muted-foreground shrink-0">{heading}</span>
          )}
          <span className="text-sm font-medium truncate">
            {trailerId || <span className="text-muted-foreground font-normal">No trailer</span>}
          </span>
        </div>
        {(trailerTypeLabel || loadLabel) && (
          <span className="text-xs text-muted-foreground capitalize text-right">
            {[trailerTypeLabel, loadLabel].filter(Boolean).join(" • ")}
          </span>
        )}
      </div>
      {(temperature || seal) && (
        <div className="pt-1">
          {temperature && (
            <Row compact={compact} label="Temperature controlled" value={temperature} />
          )}
          <Row compact={compact} label="Seal" value={seal || undefined} />
        </div>
      )}
    </div>
  );
}

export function OrderDetailsSections({
  order,
  trailerTypes,
  trailerLoadStatus,
  loadStatusHistorical,
  trailerTypeIdFallback,
  hideLegSpecific,
  eapiTripVerified,
  recurringTemplate,
  compact,
  legTrailers,
  onRegenerateExternalTrip,
  regeneratingExternalTrip,
}: Props) {
  const iconCls = "h-4 w-4";
  const o = order as any;
  const { activeTenant } = useTenant();
  const tripHref = getAuroraTripUrl(activeTenant, order.eapi_trip_id);

  const trailerTypeId = order.trailer_type_id ?? trailerTypeIdFallback ?? null;
  const trailerTypeLabel = trailerTypeId ? getTrailerTypeLabel(trailerTypeId, trailerTypes) : null;
  const resolvedLoad = resolveOrderLoadStatus({
    status: order.status,
    liveLoadStatus: trailerLoadStatus,
    historical: loadStatusHistorical,
  });
  const loadLabel = resolvedLoad.value
    ? resolvedLoad.isHistorical
      ? `${resolvedLoad.value} at delivery`
      : resolvedLoad.value
    : null;
  const isLoaded = resolvedLoad.value === "loaded";
  const isEmpty = resolvedLoad.value === "empty";
  const tempControlled = o.is_temperature_controlled;
  const tempMaintain = o.temperature_to_maintain_fahrenheit;

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* Shipment details */}
      <SectionCard title="Shipment details" icon={<Package className={iconCls} />} compact={compact}>
        <Row
          compact={compact}
          label="Billing party"
          value={
            order.billing_party?.organization_name ? (
              <div className="flex flex-col items-end">
                <span>{order.billing_party.organization_name}</span>
                {o.customer_dot_number && (
                  <span className="text-xs text-muted-foreground font-normal">
                    DOT {o.customer_dot_number}
                  </span>
                )}
              </div>
            ) : undefined
          }
        />
        <Row compact={compact} label="Tender ID" value={order.tender_id} />
        <Row compact={compact} label="BOL Number" value={o.bol_number} />
        <Row compact={compact} label="Commodity code" value={o.commodity_code} />
        <Row
          compact={compact}
          label="Notes"
          value={
            o.notes ? (
              <span className="whitespace-pre-wrap" title={o.notes}>
                {o.notes}
              </span>
            ) : undefined
          }
        />
        {!hideLegSpecific && (
          <Row
            compact={compact}
            label="External Trip ID"
            value={
              order.eapi_trip_id ? (
                <span className="inline-flex items-center gap-1.5">
                  {tripHref ? (
                    <a
                      href={tripHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {order.eapi_trip_id}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono text-xs">{order.eapi_trip_id}</span>
                  )}
                  {eapiTripVerified === true && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                  {onRegenerateExternalTrip && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={!!regeneratingExternalTrip}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRegenerateExternalTrip();
                            }}
                            aria-label="Regenerate External Trip"
                          >
                            <RefreshCw
                              className={`h-3.5 w-3.5 ${regeneratingExternalTrip ? "animate-spin" : ""}`}
                            />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Regenerate External Trip</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </span>
              ) : undefined
            }
          />
        )}
        {recurringTemplate && order.recurring_order_template_id && (
          <Row
            compact={compact}
            label="Template"
            value={
              <Link to={`/templates/${order.recurring_order_template_id}`} className="text-primary hover:underline">
                {recurringTemplate.unique_id}
              </Link>
            }
          />
        )}
      </SectionCard>

      {/* Equipment */}
      <SectionCard title="Equipment" icon={<Truck className={iconCls} />} compact={compact}>
        {hideLegSpecific && legTrailers && legTrailers.length > 0 ? (
          <div className={compact ? "space-y-2 pt-1" : "space-y-3 pt-2"}>
            {legTrailers.map((lt) => {
              const typeLabel = lt.trailer_type_id
                ? getTrailerTypeLabel(lt.trailer_type_id, trailerTypes)
                : null;
              return (
                <TrailerSubCard
                  key={lt.leg_sequence}
                  compact={compact}
                  heading={`Leg ${lt.leg_sequence}`}
                  trailerId={lt.trailer_id}
                  trailerTypeLabel={typeLabel}
                />
              );
            })}
          </div>
        ) : (
          <>
            <Row
              compact={compact}
              label="Trailer"
              value={
                order.trailer_id ? (
                  <div className="flex flex-col items-end">
                    <span>{order.trailer_id}</span>
                    {(trailerTypeLabel || loadLabel) && (
                      <span className="text-xs text-muted-foreground font-normal capitalize">
                        {[trailerTypeLabel, loadLabel].filter(Boolean).join(" • ")}
                      </span>
                    )}
                  </div>
                ) : undefined
              }
            />
            {!isEmpty && (
              <Row
                compact={compact}
                label="Temperature controlled"
                value={
                  isLoaded
                    ? tempControlled === true
                      ? tempMaintain != null
                        ? `${tempMaintain}℉`
                        : "Yes"
                      : tempControlled === false
                        ? "No"
                        : undefined
                    : undefined
                }
              />
            )}
            <Row compact={compact} label="Seal" value={o.seal_number ?? undefined} />
          </>
        )}
      </SectionCard>

      {/* Assignments */}
      {!hideLegSpecific && (
        <SectionCard title="Assignments" icon={<Users className={iconCls} />} compact={compact}>
          <Row compact={compact} label="Tractor" value={order.assigned_tractor?.name} />
          <Row
            compact={compact}
            label="Operators"
            value={
              [order.assigned_operator?.name, order.assigned_operator_2?.name].filter(Boolean).join(", ") || undefined
            }
          />
        </SectionCard>
      )}

    </div>
  );
}